import React, { useEffect, useState, useContext } from 'react';
import { Modal, Button, Icon } from 'antd';
import {useApolloClient} from 'react-apollo';
import { pauseAllPolling, resumeAllPolling } from '../apollo/pollingControl';
import { ServiceWorkerContext } from '../context/serviceworker';

const TEN_MINUTES = 10 * 60 * 1000;
const MAX_SNOOZES = 3;

function UpdatePrompt() {

  const client = useApolloClient();

  const sw = useContext(ServiceWorkerContext);
  const [visible, setVisible] = useState(false);
  const [snoozedUntil, setSnoozedUntil] = useState<number | null>(null);
  const [snoozesUsed, setSnoozesUsed] = useState<number>(0);
  const [lastUpdateFlag, setLastUpdateFlag] = useState<boolean>(false);

  const remainingSnoozes = Math.max(0, MAX_SNOOZES - snoozesUsed);

  // Show modal when updateAvailable flips true and snooze expired
  useEffect(() => {
    if (!sw) return;
    const flag = !!sw.updateAvailable;
    if (flag && flag !== lastUpdateFlag) {
      const now = Date.now();
      if (!snoozedUntil || now >= snoozedUntil) {
        setVisible(true);
        pauseAllPolling(client);
      }
    }
    setLastUpdateFlag(flag);
  }, [sw, sw?.updateAvailable, snoozedUntil, lastUpdateFlag, client]);

  // Timer to re-show after snooze
  useEffect(() => {
    if (!snoozedUntil) return;
    const now = Date.now();
    const remaining = snoozedUntil - now;
    if (remaining <= 0) return;
    const timer = setTimeout(() => {
      if (sw?.updateAvailable) {
        setVisible(true);
        pauseAllPolling(client);
      }
    }, remaining);
    return () => clearTimeout(timer);
  }, [snoozedUntil, sw, client]);

  const onUpdateNow = () => {
    if (sw?.update) {
      sw.update();
    } else {
      window.location.reload();
    }
  };

  const onRemindLater = () => {
    if (remainingSnoozes <= 0) return;
    setSnoozesUsed((c) => c + 1);
    setSnoozedUntil(Date.now() + TEN_MINUTES);
    setVisible(false);
    resumeAllPolling(client);
  };

  if (!sw) return null;

  return (
    <Modal
      visible={visible}
      closable={false}
      maskClosable={false}
      footer={[
        <Button key="later" onClick={onRemindLater} disabled={remainingSnoozes <= 0}>
          Remind me in 10 minutes
        </Button>,
        <Button key="update" type="primary" onClick={onUpdateNow}>
          <Icon type="download" /> Update now
        </Button>,
      ]}
      title={
        <span>
          <Icon type="info-circle" style={{ color: '#faad14', marginRight: 8 }} />
          A new update is available
        </span>
      }
    >
      <p>
        A newer version of this application is available. Updating will refresh your page and may interrupt any in-progress work.
      </p>
      <p style={{ marginTop: 8, opacity: 0.8 }}>
        You can snooze {remainingSnoozes} more {remainingSnoozes === 1 ? 'time' : 'times'}.
      </p>
    </Modal>
  );
}

export default UpdatePrompt;
